import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Store, Edit2, Trash2, MapPin, Power, PowerOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Stores = () => {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    location: ''
  });

  useEffect(() => {
    fetchStores();
  }, [showInactive]);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stores?include_inactive=${showInactive}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      toast.error('Error al cargar tiendas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (editingStore) {
        await axios.put(`${API}/stores/${editingStore.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tienda actualizada correctamente');
      } else {
        await axios.post(`${API}/stores`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Tienda creada correctamente');
      }
      
      fetchStores();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar tienda');
    }
  };

  const handleEdit = (store) => {
    setEditingStore(store);
    setFormData({
      name: store.name,
      location: store.location
    });
    setDialogOpen(true);
  };

  const handleToggleStatus = async (store) => {
    const action = store.is_active ? 'desactivar' : 'activar';
    if (!window.confirm(`¿Estás seguro de ${action} la tienda "${store.name}"?`)) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/stores/${store.id}/toggle-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar estado');
    }
  };

  const handleDelete = async (storeId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta tienda?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Tienda eliminada correctamente');
      fetchStores();
    } catch (error) {
      toast.error('Error al eliminar tienda');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', location: '' });
    setEditingStore(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Tiendas</h1>
          <p className="text-slate-600 dark:text-slate-400 mt-1">Gestiona las sucursales de Express Products</p>
        </div>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-slate-300"
            />
            <span className="text-slate-600 dark:text-slate-400">Mostrar inactivas</span>
          </label>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-store-button">
                <Plus className="h-4 w-4 mr-2" />
                Nueva Tienda
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingStore ? 'Editar Tienda' : 'Nueva Tienda'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="store-form">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Tienda</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ej: Productland Nuevo Chaparron"
                    required
                    data-testid="store-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Ubicacion</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    placeholder="Ej: Jutiapa, Guatemala"
                    required
                    data-testid="store-location-input"
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" data-testid="save-store-button">
                  {editingStore ? 'Actualizar' : 'Crear'} Tienda
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stores Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {stores.map((store) => (
          <Card 
            key={store.id} 
            className={`hover-lift ${!store.is_active ? 'opacity-60 border-dashed' : ''}`} 
            data-testid={`store-card-${store.id}`}
          >
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${store.is_active ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-slate-100 dark:bg-slate-800'}`}>
                  <Store className={`h-5 w-5 ${store.is_active ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`} />
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold text-slate-900 dark:text-white">
                    {store.name}
                  </CardTitle>
                  {!store.is_active && (
                    <span className="text-xs text-red-500 font-medium">INACTIVA</span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                <MapPin className="h-4 w-4 mr-2 text-slate-400" />
                {store.location}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  onClick={() => handleEdit(store)}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  data-testid={`edit-store-${store.id}`}
                >
                  <Edit2 className="h-4 w-4 mr-1" />
                  Editar
                </Button>
                <Button
                  onClick={() => handleToggleStatus(store)}
                  variant="outline"
                  size="sm"
                  className={store.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}
                  title={store.is_active ? 'Desactivar tienda' : 'Activar tienda'}
                  data-testid={`toggle-store-${store.id}`}
                >
                  {store.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                </Button>
                <Button
                  onClick={() => handleDelete(store.id)}
                  variant="outline"
                  size="sm"
                  className="text-red-600 hover:bg-red-50"
                  data-testid={`delete-store-${store.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {stores.length === 0 && (
        <Card className="p-12 text-center">
          <Store className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No hay tiendas registradas</h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">Comienza agregando tu primera sucursal</p>
          <Button onClick={() => setDialogOpen(true)} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Agregar Primera Tienda
          </Button>
        </Card>
      )}
    </div>
  );
};

export default Stores;
